/**
 * Выделение протягиванием по сетке месяца — геометрия без React.
 *
 * Клетки знают свои прямоугольники (в координатах сетки), указатель — свою точку.
 * Быстрое движение мыши или пальца даёт редкие точки, и между двумя соседними легко
 * перепрыгнуть клетку; поэтому отрезок между ними проходится с шагом меньше клетки, и
 * каждая задетая клетка попадает в выделение.
 */

export type Rect = {x: number; y: number; w: number; h: number};
export type Point = {x: number; y: number};

/** Дата клетки под точкой, или `null` — между клетками или вне сетки. */
export function hitTest(rects: ReadonlyMap<string, Rect>, pt: Point): string | null {
  for (const [date, r] of rects) {
    if (pt.x >= r.x && pt.x <= r.x + r.w && pt.y >= r.y && pt.y <= r.y + r.h) {
      return date;
    }
  }
  return null;
}

/** Точки отрезка a→b с шагом не больше `step`, включая оба конца. */
export function pointsAlong(a: Point, b: Point, step: number): Point[] {
  const dist = Math.hypot(b.x - a.x, b.y - a.y);
  const n = Math.max(1, Math.ceil(dist / Math.max(1, step)));
  const out: Point[] = [];
  for (let i = 0; i <= n; i += 1) {
    out.push({x: a.x + ((b.x - a.x) * i) / n, y: a.y + ((b.y - a.y) * i) / n});
  }
  return out;
}

/** Клетки, задетые отрезком a→b. */
export function datesAlong(
  rects: ReadonlyMap<string, Rect>,
  a: Point,
  b: Point,
  step = 8,
): string[] {
  const seen = new Set<string>();
  for (const pt of pointsAlong(a, b, step)) {
    const hit = hitTest(rects, pt);
    if (hit) {
      seen.add(hit);
    }
  }
  return [...seen];
}

/** Протягивание началось с выделенного дня — оно снимает, иначе — добавляет. */
export type PaintMode = 'add' | 'remove';

export function paint(
  selected: ReadonlySet<string>,
  dates: Iterable<string>,
  mode: PaintMode,
): Set<string> {
  const next = new Set(selected);
  for (const d of dates) {
    if (mode === 'add') {
      next.add(d);
    } else {
      next.delete(d);
    }
  }
  return next;
}

/** Сдвиг меньше этого — нажатие, а не протягивание: рука дрожит. */
export const TAP_SLOP = 6;
